import React, {useCallback, useContext, useState} from 'react';
import * as _ from 'lodash';
import DeliveryPeriod, { Bid, CommoditySettings, getDeliveryPeriodDisplayName } from 'domain/deliveryPeriod';
import Commodity from 'domain/commodity';
import PhysicalLocation, { isSelfOrChild } from 'domain/physicalLocation';
import PageFilter from 'features/pageFilter/pageFilter';
import { ColDef, CellClassParams, ColGroupDef } from '@ag-grid-community/all-modules';
import AddOfferModal from 'features/offers/AddOfferModal';
import AddOfferModel from 'features/offers/addOfferModel';
import { OfferTypeEnum } from 'domain/offerType';
import Loading from 'common/ui/Loading';
import { BidViewTypesEnum } from 'features/bidEditor/bidViewTypesEnum';
import columnBuilder, { ColDefBuilder, ColumnBuilder } from 'common/agGrid/columnBuilder';
import { formatDecimalPlace } from 'common/formatters';
import './BidsPanel.scss';
import SingleCommodityBidTable from './SingleCommodityBidTable';
import MultiCommodityBidTable from './MultiCommodityBidTable';
import { formatFuturesPrice } from 'domain/futuresContract';
import ErrorBoundary from 'common/ui/ErrorBoundary';
import { useSelector } from 'react-redux';
import IAppStore from 'app/IAppStore';
import { ContractType } from 'domain/contract';
import useScreenSize from 'common/ui/useScreenSize';
import SnapshotQuoteButton from './SnapshotQuoteButton';
import LastUpdated from 'common/ui/LastUpdated';
import { BidsContext } from './BidTable';
import { useJsonUserPreference } from 'features/userPreferences/useUserPreference';
import { isContractTypeAllowed } from 'features/contracts/helpers';
import { Unit } from 'domain/unit';
import * as NP from "number-precision";
import { roundCents } from 'common/decimal';
import { localizeBid as localize } from 'features/localization/localizeService';
import useTraders from 'features/settings/traders/useTraders';
import useDeliveryPeriods from 'features/deliveryPeriods/useDeliveryPeriods';
import useOfferings from 'features/settings/offerings/useOfferings';
import { convertPriceToSelectedUnit, roundBid } from 'external/helper';


export interface BidsPanelProps {
    locations: PhysicalLocation[];
    pageFilter: PageFilter;
    showCommodityPicker?: boolean;
}

//Declare as a rule, as .withCellClass will overrule the right alignment
const largeFontRule = { "large-font": () => true };

function buildDeliveryPeriods(hideSymbol: boolean, isPinned: boolean, useSmallDisplay: boolean): ColDef[] {
    
    const additionalProperties = isPinned && !useSmallDisplay ? { pinned: 'left' } : null;
    const deliveryName: ColDef = columnBuilder.for(localize("BidPanel_Header_DeliveryPeriod"), "name", additionalProperties)
        .withGetter(m =>  getDeliveryPeriodDisplayName(m.data))
        .withCellClass("large-font")
        .withWidth( useSmallDisplay ? 100 : 195)
        .build();

    const symbol: ColDef = columnBuilder.for(localize("BidPanel_Header_Symbol"), "contract.symbol", additionalProperties)
        .withCellClass("font-weight-bold large-font")
        .withWidth(65)
        .build();

    return hideSymbol ? [deliveryName] : [deliveryName, symbol];
}

const SETTINGS_PER_COMMODITY: string = 'Bids_SettingsPerCommodity';
const BIDS_ORDER_SELECTION: string = 'Bids_SelectedCommodityOrder';

export const BidsPanel: React.FC<BidsPanelProps> = (props: BidsPanelProps) => {
    
    const [isAddOfferModalVisible, showAddOfferModal] = useState(false);
    const [addOfferModel, setAddOfferModel] = useState<AddOfferModel>(null);


    const reduxOfferings = useOfferings();
    const myId = useSelector((m: IAppStore) => m.userState.internalId);
    const isCustomer = useSelector((m: IAppStore) => m.userState.isCustomer);
    const customerAccounts = useSelector((m: IAppStore) => m.userState.customerAccounts);
    const canMakeOffer = useSelector((m: IAppStore) => m.userState.userConfiguration.canMakeOffer);
    const deliveryPeriods = useDeliveryPeriods();
    const myCommodities = useSelector((m: IAppStore) => m.myCommodities);
    const userConfiguration = useSelector((m: IAppStore) => m.userState.userConfiguration);

    const traders = useTraders();
    const userAsTrader = traders?.find(m => m.userId === myId);
    const screenSize = useScreenSize();

    const selectedCommoditySettings = useJsonUserPreference<CommoditySettings[]>(SETTINGS_PER_COMMODITY, [])
    const selectedCommodityOrder = useJsonUserPreference<number[]>(BIDS_ORDER_SELECTION, []);

    const isLoaded = deliveryPeriods != null &&
        props.locations != null &&
        props.pageFilter != null &&
        (!isCustomer || reduxOfferings != null);

    const offerings = reduxOfferings?.filter(o => (o.allowBuying || o.allowSelling) && o.deliveryMethods.length > 0 && o.offerTypes.length > 0);

    
    const periodsByCommodity = _.groupBy(deliveryPeriods, m => m.commodity.id);
    const commoditiesWithDeliveryPeriods = myCommodities?.filter(m => periodsByCommodity[m.id] != null) ?? [];

    const physicalLocations = deliveryPeriods == null || props.locations == null
        ? []
        : _.orderBy(
            _.uniqBy(deliveryPeriods
                .flatMap(dp =>
                    dp.bids.map(b =>
                        props.locations.find(l => l.id === b.location.id)).filter(m => m != null)),
                    l => l.id),
                    l => l.name).filter(l =>
                                    props.pageFilter.locations.length === 0 ||
                                    props.pageFilter.locations.some(filterLocation => filterLocation.id === l.id)
    );


    let commodities = commoditiesWithDeliveryPeriods
            .filter(c => props.pageFilter.commodities.length === 0 || props.pageFilter.commodities.some(m => c.id === m.id));
    if(selectedCommodityOrder.value.length > 0) {
        let sortedCommodities = [] as Commodity[];
        for (const element of selectedCommodityOrder.value) {
            const commodity = commodities.find(x => x.id === element);
            if(commodity) {
                sortedCommodities.push(commodity);
            }
        }
        // Adding newly added commodities at the end which are not in selection.
        const newCommoditiesNotPresentInLastSelection = commodities.filter(c => !selectedCommodityOrder.value.includes(c.id));
        if(newCommoditiesNotPresentInLastSelection?.length > 0){
            const newSelectedCommodityOrder = [
                ...selectedCommodityOrder.value,
                ...newCommoditiesNotPresentInLastSelection.map(c => c.id)
            ];
            selectedCommodityOrder.update(newSelectedCommodityOrder);
            commodities = [
                ...sortedCommodities,
                ...newCommoditiesNotPresentInLastSelection
            ];
        }
        commodities = sortedCommodities;
    }
            
    function buildFutures(commodity: Commodity, hideFutures: boolean, isPinned: boolean, isHTAOfferAllowed: boolean): ColDef[] {
        
        if (hideFutures) return [];

        const useSmallDisplay = screenSize.isAtMost("sm");
        const additionalProperties = isPinned && !useSmallDisplay ? { pinned: 'left' } : null;

        const futuresPrice: ColDef = columnBuilder.for(localize("BidPanel_Header_Last"), null, additionalProperties)
            .withCellRenderer<DeliveryPeriod>(params => <LastPriceColumn 
                price={params.node.data.contract.price}
                isSettled={params.node.data.contract?.isSettled}
                showLink={isHTAOfferAllowed && canMakeOffer && commodity.publicBidDisplay !== BidViewTypesEnum.basisOnly && canManageAccounts()}
                onClick={() => addHTAOfferFromBid(params.node.data)} />)
            .withWidth(75)
            .asRightAligned()
            .withHeaderComponent(LastHeader)
            .build();

        const cols = [futuresPrice];

        if (commodity.priceProtection != null) {
            const protection = columnBuilder.for(localize("BidPanel_Header_Protection"), null, additionalProperties)
                .withGetter(params => params.data.contract?.isSettled ? commodity.priceProtection : null)
                .withWidth(70)
                .asDecimal()
                .build()
            cols.push(protection);
        }

        if (!useSmallDisplay) {
            const high: ColDef = columnBuilder.for(localize("BidPanel_Header_High"), "contract.high", additionalProperties)
                .withCellClassRules(largeFontRule)
                .withWidth(65)
                .asRightAligned()
                .asDecimal('0.0000')
                .build();
    
            const low: ColDef = columnBuilder.for(localize("BidPanel_Header_Low"), "contract.low", additionalProperties)
                .withCellClassRules(largeFontRule)
                .withWidth(65)
                .asRightAligned()
                .asDecimal('0.0000')
                .build();
        
    
            const netChange: ColDef = columnBuilder.for(localize("BidPanel_Header_NetChange"), "contract.netChange", additionalProperties)
                .withCellClassRules({
                    "font-weight-bold": () => true,
                    "large-font": () => true,
                    "net-change-no-change": (m: CellClassParams) => (m.value ?? 0) === 0,
                    "net-change-positive-change": (m: CellClassParams) => m.value > 0,
                    "net-change-negative-change": (m: CellClassParams) => m.value < 0
                })
                .withWidth(65)
                .asDecimal('0.0000')
                .build();

            cols.splice(0, 0, high, low);
            cols.push(netChange);
        }
        
        return cols;
    }
    
    function buildBids(builder: ColumnBuilder, location: PhysicalLocation, commodity: Commodity, isBasisContractAllowed: boolean, isCashOfferAllowed: boolean, selectedUnit: Unit): ColDefBuilder[] {
    
        const basisColumn: ColDefBuilder = builder.for(localize("BidPanel_Header_Basis"), null)
            .asRightAligned()
            .withWidth(commodity.publicBidDisplay === BidViewTypesEnum.basisOnly ? 130 : 65);
    
        const cashColumn: ColDefBuilder = builder.for(localize("BidPanel_Header_Cash"), null)
            .asRightAligned()
            .withWidth(commodity.publicBidDisplay === BidViewTypesEnum.cashOnly ? 130 : 65);

        const isLocationAccessible = userAsTrader == null || (location != null && userAsTrader?.locations.some(l => l.id === location.id));
        
        if (commodity.publicBidDisplay === BidViewTypesEnum.futuresOnly) {
            basisColumn.withFormatter(() => "--");
            cashColumn.withFormatter(() => "--");
        }
        else {
            basisColumn.withGetter(m => {
                    const bid: Bid = m.data.bids.find((b: Bid) => b.location.id === location?.id);
                    return bid == null ? null : {
                        locationId: bid.location.id,
                        basis: bid.basis
                    };
                })
                .withValueCellRenderer<DeliveryPeriod, {locationId: string, basis: number}>(renderer => 
                    <BasisColumn 
                        basis={renderer.value?.basis != null ? convertPriceToSelectedUnit(renderer.value.basis, renderer.node.data.commodity, selectedUnit) : null}
                        showLink={isBasisContractAllowed && canMakeOffer && isLocationAccessible && canManageAccounts()}
                        onClick={() => addBasisContractFromBid(renderer.node.data, renderer.value.locationId)} />);
            cashColumn.withGetter(m => {
                    const bid: Bid = m.data.bids.find((b: Bid) => b.location.id === location?.id);
                    return bid == null ? null : {
                        locationId: bid.location.id,
                        cash: bid.cash
                    };
                })
                .withValueCellRenderer<DeliveryPeriod, {locationId: string, cash: number}>(renderer => 
                    <CashColumn 
                        cash={renderer.value?.cash != null ? roundBid(renderer.value.cash, renderer.node.data.contract?.isSettled ,renderer.node.data.commodity, selectedUnit) : null}
                        showLink={isCashOfferAllowed && canMakeOffer && isLocationAccessible && canManageAccounts()}
                        onClick={() => addCashOfferFromBid(renderer.node.data, renderer.value.locationId)} />)
        }
    
        if (commodity.publicBidDisplay === BidViewTypesEnum.cashOnly) {
            return [cashColumn];
        }
        else if (commodity.publicBidDisplay === BidViewTypesEnum.basisOnly) {
            return [basisColumn];
        }
        else
            return [basisColumn, cashColumn];
    }

    function canManageAccounts() {
        if (isCustomer && customerAccounts.length > 0) {
            return customerAccounts.some(x => x.canManage);
        }
        return true;
    }
    
    function getColumnDefinitions(commodity: Commodity, locations: PhysicalLocation[], showLocationHeader: boolean, selectedUnit: Unit): ColDef[] {
        const locationsForCommodity = locations.filter((l: PhysicalLocation) => l.commodities?.some(c => c.id === commodity.id));
        let isBasisContractAllowed = isContractTypeAllowed(ContractType.Basis, userConfiguration);
        let isCashOfferAllowed = isContractTypeAllowed(ContractType.Cash, userConfiguration);
        let isHTAOfferAllowed = isContractTypeAllowed(ContractType.Hta, userConfiguration);
        const isCashOnly = commodity.isCashOnly && commodity.publicBidDisplay === BidViewTypesEnum.cashOnly;

        if (!showLocationHeader){
            if(isCustomer)
            {
                isBasisContractAllowed = isOfferingSetForContract(locations[0], commodity, ContractType.Basis);
                isCashOfferAllowed = isOfferingSetForOffer(locations[0], commodity, OfferTypeEnum.Cash);
                isHTAOfferAllowed = isOfferingSetForOffer(locations[0], commodity, OfferTypeEnum.Hta);
            }
            return [
                ...buildDeliveryPeriods(isCashOnly, false, screenSize.isAtMost("sm")),
                ...buildFutures(commodity, isCashOnly, false, isHTAOfferAllowed),
                ...columnBuilder.collection(builder => buildBids(builder, locations[0], commodity, isBasisContractAllowed, isCashOfferAllowed, selectedUnit))
            ];
        }
        
        if(isCustomer)
        {
            isHTAOfferAllowed = isOfferingSetForOffer(null, commodity, OfferTypeEnum.Hta, locationsForCommodity);
        }

        const columns: ColGroupDef[] = [
            {
                children: buildDeliveryPeriods(isCashOnly, true, screenSize.isAtMost("sm")),
                headerClass: 'pl-1',
                headerGroupComponent: DeliveryPeriodHeader
            },
            {
                headerName: localize("BidPanel_Header_Futures"),
                headerClass: 'top-header',
                children:  buildFutures(commodity, isCashOnly, true, isHTAOfferAllowed)
            }
        ];

        locationsForCommodity.forEach(l => {
            if(isCustomer)
            {
                isBasisContractAllowed = isOfferingSetForContract(l, commodity, ContractType.Basis);
                isCashOfferAllowed = isOfferingSetForOffer(l, commodity, OfferTypeEnum.Cash);
            }            
            columns.push(
                {
                    headerName: l.name,
                    headerClass: 'top-header',
                    children: columnBuilder.collection(builder => buildBids(builder, l, commodity, isBasisContractAllowed, isCashOfferAllowed, selectedUnit))
                }
            );
        });
    
        return columns;
    }

    function isOfferingSetForOffer(location: PhysicalLocation, commodity: Commodity, offerType: OfferTypeEnum, searchLocations: PhysicalLocation[] = []) : boolean {
        if(searchLocations.length > 0)
        {
            return offerings.some(o => searchLocations.find(l => l.id === o.location.id) && o.commodity.id === commodity.id && o.offerTypes.includes(offerType));
        }
        return offerings.some(o => o.location.id === location?.id && o.commodity.id === commodity.id && o.offerTypes.includes(offerType));
    }

    function isOfferingSetForContract(location: PhysicalLocation, commodity: Commodity, contractType: ContractType) : boolean {
        return offerings.some(o => o.location.id === location?.id && o.commodity.id === commodity.id && o.contractTypes.includes(contractType));
    }


    function addCashOfferFromBid(deliveryPeriod: DeliveryPeriod, locationId: string) {
        const bid = deliveryPeriod.bids.find(m => m.location.id === locationId);
        const model: AddOfferModel = {
            commodity: deliveryPeriod.commodity,
            customerWillSell: true,
            deliveryPeriod: deliveryPeriod,
            location: bid.location,
            offerType: OfferTypeEnum.Cash,
            price: bid.cash,
            basis: bid.basis
        };
        setAddOfferModel(model);
        showAddOfferModal(true);
    }

    function addHTAOfferFromBid(deliveryPeriod: DeliveryPeriod) {
        const model: AddOfferModel = {
            commodity: deliveryPeriod.commodity,
            customerWillSell: true,
            deliveryPeriod: deliveryPeriod,
            price: deliveryPeriod.contract.price,
            offerType: OfferTypeEnum.Hta
        };
        setAddOfferModel(model);
        showAddOfferModal(true);
    }

    function addBasisContractFromBid(deliveryPeriod: DeliveryPeriod, locationId: string) {
        const bid = deliveryPeriod.bids.find(m => m.location.id === locationId);
        const model: AddOfferModel = {
            commodity: deliveryPeriod.commodity,
            customerWillSell: true,
            deliveryPeriod: deliveryPeriod,
            location: bid.location,
            offerType: OfferTypeEnum.BasisContract,
            basis: bid.basis
        };
        setAddOfferModel(model);
        showAddOfferModal(true);
    }

    const getColumnsForCommodity = useCallback(function (commodity: Commodity, showLocationHeader: boolean): ColDef[] {
        const locations = getCommodityLocations(commodity);
        const selectedUnit = selectedCommoditySettings.value.find(x => x.commodityId == commodity.id)?.selectedUnit ?? commodity.baseUnit;
        return getColumnDefinitions(commodity, locations, showLocationHeader, selectedUnit);
    }, [getCommodityLocations, selectedCommoditySettings.value, offerings]);

    function getCommodityLocations(commodity: Commodity): PhysicalLocation[] {
        
        if(props.pageFilter.locations.length === 1) {
            return physicalLocations.filter(pl => pl.id === props.pageFilter.locations[0].id)
        }

        const userSelectedCommodityRegions = selectedCommoditySettings.value.find(c => c.commodityId === commodity.id);
        if(userSelectedCommodityRegions != null) {
            const region = userSelectedCommodityRegions.regions.find(rl => rl.isSelected);
            if(region != null && region.locationIds.length > 0) {
                let locations = [] as PhysicalLocation[];
                for (const userSelectedLocation of region.locationIds) {
                    const loc = physicalLocations.find(x => x.id === userSelectedLocation);
                    if(loc) {
                        locations.push(loc);
                    }
                }
                return locations;
            }
            else {
                return physicalLocations.filter(l => isSelfOrChild(l, region.regionId));
            }
        }

        return physicalLocations;
    }


    function onClose() {
        showAddOfferModal(false);
        setAddOfferModel(null);
    }

    if (!isLoaded) return <Loading />;

    return <ErrorBoundary>
        <div className="bids-panel">
            {props.pageFilter.locations.length === 1
                ? <SingleCommodityBidTable 
                    commodities={commodities} 
                    periodsByCommodity={periodsByCommodity} 
                    getColumnDefinitions={getColumnsForCommodity}
                    locations={physicalLocations}
                    selectedCommodityOrder={selectedCommodityOrder.value}
                    saveCommodityOrder={selectedCommodityOrder.update}
                    selectedCommoditySettings={selectedCommoditySettings.value}
                    saveCommoditySettingsChanges={selectedCommoditySettings.update}
                    isCustomer={isCustomer}
                    /> 
                : <MultiCommodityBidTable  
                    commodities={commodities} 
                    periodsByCommodity={periodsByCommodity} 
                    getColumnDefinitions={getColumnsForCommodity} 
                    showCommodityPicker={props.showCommodityPicker}
                    locations={physicalLocations}
                    selectedCommodityOrder={selectedCommodityOrder.value}
                    saveCommodityOrder={selectedCommodityOrder.update}
                    selectedCommoditySettings={selectedCommoditySettings.value}
                    saveCommoditySettingsChanges={selectedCommoditySettings.update}
                    isCustomer={isCustomer}
                    />
            }
            {isAddOfferModalVisible && <AddOfferModal
                        model={addOfferModel}
                        roundCashPrice
                        onClose={onClose} />
            }
        </div>
    </ErrorBoundary>;
};

const LastHeader: React.FC<any> = () => {

    return <div className="ag-header-cell-label">
        <SnapshotQuoteButton />
        <span className="ag-header-cell-text">Last</span>
    </div>
}

const DeliveryPeriodHeader: React.FC<any> = () => {
    const bidsContext = useContext(BidsContext);

    return <LastUpdated lastUpdated={bidsContext.lastUpdatedDate} label="Prices as of" />;
}

interface LastPriceColumnProps {
    price: number;
    isSettled: boolean;
    showLink: boolean;
    onClick(): void;
}
const LastPriceColumn: React.FC<LastPriceColumnProps> = React.memo(props => props.showLink ? 
        <button className="link-button font-weight-bold large-font" onClick={props.onClick} >
            {formatFuturesPrice(props.price, props.isSettled)}
        </button>
        : <div>{formatFuturesPrice(props.price, props.isSettled)}</div>,
        (oldProps, newProps) => oldProps.price === newProps.price && 
            oldProps.isSettled === newProps.isSettled && 
            oldProps.showLink === newProps.showLink);

   
interface BasisColumnProps {
    basis?: number;
    showLink: boolean;
    onClick(): void;
}

const BasisColumn: React.FC<BasisColumnProps> = React.memo(props => props.basis == null
    ? <>--</>
    : (props.showLink ? 
        <button className="link-button large-font" onClick={props.onClick}>
            {formatDecimalPlace(props.basis)}
        </button>
        : <div>{formatDecimalPlace(props.basis)}</div>), 
    (oldProps, newProps) => oldProps.basis === newProps.basis && oldProps.showLink === newProps.showLink);
        
interface CashColumnProps {
    cash?: number;
    showLink: boolean;
    onClick(): void;
}
const CashColumn: React.FC<CashColumnProps> = React.memo(props => props.cash == null
    ? <>--</>
    : (props.showLink ? 
        <button className="link-button large-font" onClick={props.onClick}>
            {formatDecimalPlace(props.cash)}
        </button>
        : <div>{formatDecimalPlace(props.cash)}</div>),
    (oldProps, newProps) => oldProps.cash === newProps.cash && oldProps.showLink === newProps.showLink);
